import React, { useState } from 'react';
import {
    Box,
    TextField,
    Button,
    Tooltip,
    Typography
} from '@mui/material';
import { roomSettingsService } from '../../../client/room/roomSettingsService';
import toast from 'react-hot-toast';
import type { IRoomSettings, IRoomPermissions } from '../../../client/room/types';
import { useTranslation } from '@fanfanlo';
import { useProxyWatch } from '@fanfanlo';
import { roomData } from '../../../client/room/data';

export const PermissionSettingsContent = ({ roomId }: { roomId: string }) => {
    const { t } = useTranslation('homepage/im/components/room-settings/permission/content');
    const [loading, setLoading] = useState(false);
    const [currentSettings] = useProxyWatch(
        roomData.roomSettings.settings,
        [roomId],
        roomData.roomSettings.settings[roomId]
    );
    const [formData, setFormData] = useState<IRoomPermissions>({
        sendMessages: currentSettings?.permissions?.sendMessages ?? 0,
        invite: currentSettings?.permissions?.invite ?? 50,
        kick: currentSettings?.permissions?.kick ?? 50,
        ban: currentSettings?.permissions?.ban ?? 50,
        roomSettings: currentSettings?.permissions?.roomSettings ?? 50,
    });

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            setLoading(true);
            await roomSettingsService.updateRoomPermissions(roomId, formData);
            toast.success(t('PermissionSettingsContent.toast.success'));
        } catch (error: any) {
            toast.error(error.message || t('PermissionSettingsContent.toast.error'));
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: Number(value)
        }));
    };

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ '& > *': { mb: 2 } }}>
            <Tooltip title={t('PermissionSettingsContent.form.sendMessages.tooltip')}>
                <TextField
                    fullWidth
                    type="number"
                    label={t('PermissionSettingsContent.form.sendMessages.label')}
                    name="sendMessages"
                    value={formData.sendMessages}
                    onChange={handleChange}
                    disabled={loading}
                    InputProps={{
                        inputProps: { min: 0, max: 100 }
                    }}
                />
            </Tooltip>

            <Tooltip title={t('PermissionSettingsContent.form.invite.tooltip')}>
                <TextField
                    fullWidth
                    type="number"
                    label={t('PermissionSettingsContent.form.invite.label')}
                    name="invite"
                    value={formData.invite}
                    onChange={handleChange}
                    disabled={loading}
                    InputProps={{
                        inputProps: { min: 0, max: 100 }
                    }}
                />
            </Tooltip>

            <Tooltip title={t('PermissionSettingsContent.form.kick.tooltip')}>
                <TextField
                    fullWidth
                    type="number"
                    label={t('PermissionSettingsContent.form.kick.label')}
                    name="kick"
                    value={formData.kick}
                    onChange={handleChange}
                    disabled={loading}
                    InputProps={{
                        inputProps: { min: 0, max: 100 }
                    }}
                />
            </Tooltip>

            <Tooltip title={t('PermissionSettingsContent.form.ban.tooltip')}>
                <TextField
                    fullWidth
                    type="number"
                    label={t('PermissionSettingsContent.form.ban.label')}
                    name="ban"
                    value={formData.ban}
                    onChange={handleChange}
                    disabled={loading}
                    InputProps={{
                        inputProps: { min: 0, max: 100 }
                    }}
                />
            </Tooltip>

            <Tooltip title={t('PermissionSettingsContent.form.roomSettings.tooltip')}>
                <TextField
                    fullWidth
                    type="number"
                    label={t('PermissionSettingsContent.form.roomSettings.label')}
                    name="roomSettings"
                    value={formData.roomSettings}
                    onChange={handleChange}
                    disabled={loading}
                    InputProps={{
                        inputProps: { min: 0, max: 100 }
                    }}
                />
            </Tooltip>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                {t('PermissionSettingsContent.description')}
            </Typography>

            <Button
                type="submit"
                variant="contained"
                disabled={loading}
            >
                {t('PermissionSettingsContent.form.submit')}
            </Button>
        </Box>
    );
}; 